import { FC, ReactNode, useCallback, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import {
  TopicSentiment,
  sentimentFractions,
} from 'src/interfaces/topics.interface'
import styles from './sentimentHoverCard.module.scss'

interface SentimentHoverCardProps {
  children: ReactNode
  className?: string
  sentiment: TopicSentiment
  total: number
  // The card's heading: "Total Mentions" unless the host counts something
  // else ("Total Posts", "Total Comments").
  totalLabel?: string
}

const ROWS: { color: string; key: keyof TopicSentiment; label: string }[] = [
  { color: '#8080FF', key: 'positive', label: 'Positive' },
  { color: '#C7CED1', key: 'neutral', label: 'Neutral' },
  { color: '#FF6833', key: 'negative', label: 'Negative' },
]

const CARD_WIDTH = 258
const CARD_HEIGHT = 94
const GAP = 10
const EDGE = 8

/**
 * Wraps anything hoverable and shows the shared sentiment breakdown above it.
 *
 * Portalled and positioned from the target's viewport rect: the callers sit
 * inside `zoom: 0.9` containers with their own `overflow`, either of which
 * would clip or mis-place an absolutely positioned child.
 */
export const SentimentHoverCard: FC<SentimentHoverCardProps> = ({
  children,
  className,
  sentiment,
  total,
  totalLabel = 'Total Mentions',
}) => {
  const anchorRef = useRef<HTMLSpanElement>(null)
  const [position, setPosition] = useState<{
    left: number
    top: number
  } | null>(null)

  const show = useCallback(() => {
    const node = anchorRef.current
    if (!node) return
    const rect = node.getBoundingClientRect()
    // Centred over the target and above it, then clamped so it cannot run off
    // either edge of the viewport — the rightmost table column and the top row
    // both sit close enough to an edge for this to matter.
    const rawLeft = rect.left + rect.width / 2 - CARD_WIDTH / 2
    const left = Math.min(
      Math.max(EDGE, rawLeft),
      window.innerWidth - CARD_WIDTH - EDGE
    )
    const above = rect.top - CARD_HEIGHT - GAP
    const top = above < EDGE ? rect.bottom + GAP : above
    setPosition({ left, top })
  }, [])

  const hide = useCallback(() => setPosition(null), [])

  const fractions = sentimentFractions(sentiment)
  // Bars are read against the largest share so the leading sentiment fills the
  // column; against the raw percentage all three would look stunted.
  const max = Math.max(
    fractions.positive,
    fractions.neutral,
    fractions.negative
  )

  return (
    <>
      <span
        className={className}
        onBlur={hide}
        onFocus={show}
        onMouseEnter={show}
        onMouseLeave={hide}
        ref={anchorRef}
      >
        {children}
      </span>

      {position &&
        createPortal(
          <div
            className={styles.card}
            style={{ left: position.left, top: position.top }}
          >
            <span className={styles.title}>
              {totalLabel}: <strong>{total.toLocaleString()}</strong>
            </span>
            <div className={styles.body}>
              <div className={styles.bars}>
                {ROWS.map((row) => (
                  <span className={styles.barTrack} key={row.key}>
                    <span
                      className={styles.barFill}
                      style={{
                        background: row.color,
                        width: `${max > 0 ? (fractions[row.key] / max) * 100 : 0}%`,
                      }}
                    />
                  </span>
                ))}
              </div>
              <div className={styles.rows}>
                {ROWS.map((row) => (
                  <span className={styles.row} key={row.key}>
                    <strong className={styles.pct}>
                      {Math.round(fractions[row.key] * 100)}%
                    </strong>
                    <span className={styles.label}>{row.label}</span>
                    <span className={styles.count}>
                      {Math.round(sentiment[row.key] ?? 0).toLocaleString()}
                    </span>
                  </span>
                ))}
              </div>
            </div>
          </div>,
          document.body
        )}
    </>
  )
}
